"use client";

import { cn } from "@/lib/utils";

export type TierVariant = "free" | "player" | "pro" | "enterprise";

export interface TierCardProps {
  /** Tier variant */
  variant: TierVariant;
  /** Whether this tier is highlighted/featured */
  featured?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Card content */
  children: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
}

const variantStyles: Record<TierVariant, { bg: string; border: string; icon: string; text: string }> = {
  free: {
    bg: "bg-card/50",
    border: "border-border/50",
    icon: "text-slate-400",
    text: "text-foreground",
  },
  player: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    icon: "text-emerald-500",
    text: "text-emerald-400",
  },
  pro: {
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    icon: "text-amber-500",
    text: "text-amber-400",
  },
  enterprise: {
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    icon: "text-purple-500",
    text: "text-purple-400",
  },
};

/**
 * TierCard - Card for displaying subscription/pricing tiers
 *
 * Use for pricing tables and tier selection interfaces.
 *
 * @example
 * <TierCard variant="player" featured>
 *   <Trophy className="h-6 w-6 mb-2" />
 *   <span className="font-semibold">Member $8.99/mo</span>
 *   <span className="text-xs text-muted-foreground">Challenge & connect</span>
 * </TierCard>
 */
export function TierCard({
  variant,
  featured = false,
  className,
  children,
  onClick,
}: TierCardProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        "flex flex-col items-center p-4 rounded-lg border transition-all duration-200",
        styles.bg,
        featured ? `border-2 ${styles.border}` : styles.border,
        onClick && "cursor-pointer hover:shadow-md",
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/**
 * Get tier-specific styling classes
 */
export function getTierStyles(variant: TierVariant) {
  return variantStyles[variant];
}

export interface TierIconProps {
  /** Icon component */
  icon: React.ComponentType<{ className?: string }>;
  /** Tier variant for coloring */
  variant: TierVariant;
  /** Size of the icon container */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: { container: "p-2", icon: "h-5 w-5" },
  md: { container: "p-3", icon: "h-8 w-8" },
  lg: { container: "p-4", icon: "h-10 w-10" },
};

/**
 * TierIcon - Icon with tier-colored background
 */
export function TierIcon({
  icon: Icon,
  variant,
  size = "md",
  className,
}: TierIconProps) {
  const styles = variantStyles[variant];
  const sizeStyle = sizeClasses[size];

  return (
    <div
      className={cn(
        "rounded-full",
        styles.bg,
        styles.icon,
        sizeStyle.container,
        className
      )}
    >
      <Icon className={sizeStyle.icon} />
    </div>
  );
}

export default TierCard;

